// Google Cloud Identity Platform auth for GCIP-backed tenants (aquipe).
//
// Deliberately implemented against the GCIP REST endpoints rather than the
// Firebase JS SDK. Email/password sign-in needs exactly two endpoints, and the
// SDK would add a sizeable dependency to a bundle every tenant downloads —
// including the eight Cognito tenants that will never call this code. It also
// keeps package.json/yarn.lock untouched, which matters while other sessions
// have those files in flight.
//
// If/when Google Workspace federation lands (it needs an OAuth client that can
// only be created in the console), revisit: the redirect flow is the point where
// the SDK starts earning its weight.

import { getGcipConfig } from "../config/product";

const IDP = "https://identitytoolkit.googleapis.com/v1";
const SECURETOKEN = "https://securetoken.googleapis.com/v1";

// Session lives in localStorage so a refresh doesn't sign the user out. Only
// tokens are stored — no password ever touches storage.
const KEY = "aeronet.gcip.session";

// Refresh this long before actual expiry so an in-flight request can't race the
// boundary and 401 on a token that was valid when the call was assembled.
const REFRESH_SKEW_MS = 5 * 60 * 1000;

interface StoredSession {
  idToken: string;
  refreshToken: string;
  expiresAt: number; // epoch ms
  email: string;
}

export interface GcipUser {
  email: string;
  aeronetUserId?: number;
  aeronetCompanyId?: number;
}

function read(): StoredSession | null {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as StoredSession) : null;
  } catch {
    return null;
  }
}

function write(s: StoredSession | null): void {
  try {
    if (s) localStorage.setItem(KEY, JSON.stringify(s));
    else localStorage.removeItem(KEY);
  } catch {
    /* private browsing / storage disabled — session simply won't persist */
  }
}

/** Decode a JWT payload without verifying. Display only — the BACKEND verifies. */
function decode(token: string): Record<string, unknown> {
  try {
    const part = token.split(".")[1];
    const json = atob(part.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(decodeURIComponent(escape(json)));
  } catch {
    return {};
  }
}

async function call(url: string, body: unknown): Promise<any> {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    // GCIP returns machine codes like EMAIL_NOT_FOUND / INVALID_LOGIN_CREDENTIALS.
    throw new Error(data?.error?.message || `HTTP ${res.status}`);
  }
  return data;
}

export async function signInWithPassword(
  email: string,
  password: string
): Promise<void> {
  const { apiKey } = getGcipConfig();
  const data = await call(`${IDP}/accounts:signInWithPassword?key=${apiKey}`, {
    email,
    password,
    returnSecureToken: true,
  });
  write({
    idToken: data.idToken,
    refreshToken: data.refreshToken,
    expiresAt: Date.now() + Number(data.expiresIn || 3600) * 1000,
    email: data.email || email,
  });
}

export async function signInWithGoogleCredential(
  credential: string
): Promise<void> {
  const { apiKey } = getGcipConfig();
  const data = await call(`${IDP}/accounts:signInWithIdp?key=${apiKey}`, {
    postBody: `id_token=${encodeURIComponent(credential)}&providerId=google.com`,
    requestUri: window.location.origin,
    returnSecureToken: true,
  });
  write({
    idToken: data.idToken,
    refreshToken: data.refreshToken,
    expiresAt: Date.now() + Number(data.expiresIn || 3600) * 1000,
    email: data.email || "",
  });
}


async function refresh(session: StoredSession): Promise<StoredSession> {
  const { apiKey } = getGcipConfig();
  const res = await fetch(`${SECURETOKEN}/token?key=${apiKey}`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: `grant_type=refresh_token&refresh_token=${encodeURIComponent(
      session.refreshToken
    )}`,
  });
  if (!res.ok) {
    // Refresh token revoked or expired — force a clean re-login rather than
    // leaving a half-dead session that 401s every request.
    write(null);
    throw new Error("SESSION_EXPIRED");
  }
  const data = await res.json();
  const next: StoredSession = {
    idToken: data.id_token,
    refreshToken: data.refresh_token || session.refreshToken,
    expiresAt: Date.now() + Number(data.expires_in || 3600) * 1000,
    email: session.email,
  };
  write(next);
  return next;
}

/** Current ID token, refreshed if near expiry. Null when signed out. */
export async function getIdToken(): Promise<string | null> {
  let s = read();
  if (!s) return null;
  if (Date.now() >= s.expiresAt - REFRESH_SKEW_MS) {
    try {
      s = await refresh(s);
    } catch {
      return null;
    }
  }
  return s.idToken;
}

/**
 * Refresh now, whatever the stored expiry says. For a request the backend has
 * already rejected (401): the local clock can think a token is still good when
 * the server does not. False when there is no session or the refresh token is
 * dead (refresh() has then already cleared the session).
 */
export async function forceRefresh(): Promise<boolean> {
  const s = read();
  if (!s) return false;
  try {
    await refresh(s);
    return true;
  } catch {
    return false;
  }
}

export function getCurrentUser(): GcipUser | null {
  const s = read();
  if (!s) return null;
  const claims = decode(s.idToken);
  const num = (v: unknown) =>
    v === undefined || v === null ? undefined : Number(v);
  return {
    email: (claims.email as string) || s.email,
    aeronetUserId: num(claims["custom:aeronet_user_id"]),
    aeronetCompanyId: num(claims["custom:aeronet_company_id"]),
  };
}

export function isSignedIn(): boolean {
  return read() !== null;
}

export function signOut(): void {
  write(null);
}

/**
 * True when the signed-in token carries the claims the backend requires.
 *
 * auth.py::decode_claims pops custom:aeronet_user_id and
 * custom:aeronet_company_id off EVERY token and cannot build a User without
 * them. GCIP has no console field for custom claims — they are written by
 * repo/scripts/install/gcip_provision_user.py — so an account created by hand
 * authenticates perfectly and then fails every API call. Surfacing it at the
 * gate turns a confusing cascade of 401/500s into one honest message.
 */
export function hasRequiredClaims(): boolean {
  const u = getCurrentUser();
  return !!u && u.aeronetUserId !== undefined && u.aeronetCompanyId !== undefined;
}
